import React, { useEffect } from 'react';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { Box, Dialog, Typography } from '@mui/material';

/**
 * Shared failure dialog, rebuilt on MUI 6 + the cardiac theme — the visual pair of Success,
 * which nearly every caller renders alongside it. Same contract: shown while
 * `isFailedModalOpen`, auto-closes after 1.5s.
 *
 * The timer was previously started during render AND above the `isFailedModalOpen` early
 * return, so it ran even when the dialog was closed and re-queued on every re-render. It now
 * lives in an effect with cleanup, only while open.
 */

interface FailedProps {
  handleCloseModal?: () => void;
  title?: string;
  isFailedModalOpen?: boolean;
}

const AUTO_CLOSE_MS = 1500;

const Failed = ({ handleCloseModal, title, isFailedModalOpen }: FailedProps) => {
  useEffect(() => {
    if (!isFailedModalOpen) {
      return undefined;
    }
    const timer = setTimeout(() => handleCloseModal?.(), AUTO_CLOSE_MS);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFailedModalOpen]);

  if (!isFailedModalOpen) {
    return null;
  }

  return (
    <Dialog
      open
      onClose={handleCloseModal}
      maxWidth="xs"
      fullWidth
      slotProps={{
        backdrop: {
          sx: { backgroundColor: 'rgba(2, 8, 15, 0.72)', backdropFilter: 'blur(3px)' },
        },
        paper: {
          sx: theme => ({
            borderRadius: 3,
            border: `1px solid ${theme.palette.cardiac.line}`,
            backgroundImage: `linear-gradient(180deg, ${theme.palette.cardiac.surface2} 0%, ${theme.palette.cardiac.surface} 100%)`,
            boxShadow: '0 24px 64px rgba(0, 0, 0, 0.55)',
          }),
        },
      }}
    >
      <Box sx={{ px: 4, py: 5, display: 'grid', justifyItems: 'center', gap: 2.5 }}>
        <Box
          sx={theme => ({
            width: 76,
            height: 76,
            borderRadius: '50%',
            display: 'grid',
            placeItems: 'center',
            color: theme.palette.cardiac.red,
            border: `2px solid ${theme.palette.cardiac.red}`,
            backgroundColor: 'rgba(251, 113, 133, 0.10)',
            boxShadow: '0 0 0 8px rgba(251, 113, 133, 0.06)',
          })}
        >
          <CloseRoundedIcon sx={{ fontSize: 44 }} />
        </Box>
        <Typography
          sx={{
            fontSize: 18,
            fontWeight: 600,
            textAlign: 'center',
            lineHeight: 1.45,
            color: 'text.primary',
          }}
        >
          {title}
        </Typography>
      </Box>
    </Dialog>
  );
};

export default Failed;
